import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Plus, Trash, CalendarIcon, AlertCircle } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { useNPOCountries, useNPOStates, useNPODistricts } from "@/hooks/useNPOsApi";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Custom Components
import { LocationSelector } from "./LocationSelector";
import { OpportunitySelector } from "./OpportunitySelector";

// Types and Schema
import { campaignFormSchema, CampaignFormValues, Location } from "./CampaignFormSchema";

interface Opportunity {
  id: string;
  name: string;
  meta?: {
    description?: string;
    focus_area?: string;
    beneficiary_type?: string;
    format?: string;
  };
}

interface CampaignFormProps {
  initialData?: any;
  initialGoals?: string[];
  initialLocations?: Location[];
  initialSelectedOpportunities?: string[];
  opportunities: Opportunity[];
  isLoadingOpportunities: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  onSubmit: (data: CampaignFormValues, goals: string[], locations: Location[], selectedOpportunities: string[]) => void;
  submitButtonText: string;
  isSubmitting: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
}

export function CampaignForm({
  initialData,
  initialGoals,
  initialLocations,
  initialSelectedOpportunities,
  opportunities,
  isLoadingOpportunities,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  onSubmit,
  submitButtonText,
  isSubmitting,
  searchQuery,
  onSearchChange
}: CampaignFormProps) {
  // Initialize form with default values or provided initialData
  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: initialData || {
      name: "",
      meta: {
        description: "",
        theme: "",
      },
      active_from: new Date(),
      active_to: new Date(new Date().setDate(new Date().getDate() + 30)),
      is_active: true,
      additional_details: "",
    },
    mode: "onChange",
  });

  // State for multi-step form
  const [currentStep, setCurrentStep] = useState<string>("campaign-info");
  const [goals, setGoals] = useState<string[]>(initialGoals || [""]);
  const [locations, setLocations] = useState<Location[]>(initialLocations || [{
    country: "",
    state: "",
    district: "",
    volunteers: 1,
    details: ""
  }]);
  const [selectedOpportunities, setSelectedOpportunities] = useState<string[]>(initialSelectedOpportunities || []);
  const [stepErrors, setStepErrors] = useState<{
    campaignInfo: boolean;
    opportunities: boolean;
    locations: boolean;
  }>({
    campaignInfo: false,
    opportunities: false,
    locations: false,
  });

  // State for opportunity filters
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // State for location filters
  const [locationFilters, setLocationFilters] = useState<{
    country: string | null;
    state: string | null;
    district: string | null;
    locationIndex: number | null;
  }>({
    country: null,
    state: null,
    district: null,
    locationIndex: null
  });

  // Fetch countries for location selection
  const { data: countriesData, isLoading: isLoadingCountries } = useNPOCountries();
  const countries = countriesData?.data || [];
  
  // Fetch states based on selected country
  const { data: statesData, isLoading: isLoadingStates } = useNPOStates(
    locationFilters.country || undefined
  );
  const states = statesData?.data || [];
  
  // Fetch districts for the current location filter
  const { data: districtsData, isLoading: isLoadingDistricts } = useNPODistricts(
    locationFilters.state || undefined,
    locationFilters.country || undefined
  );
  
  // State for tracking location-specific districts
  const [locationDistricts, setLocationDistricts] = useState<{ [locationIndex: number]: string[] }>({});
  
  // Update location districts when a location's state changes
  useEffect(() => {
    if (districtsData?.data && locationFilters.locationIndex !== null) {
      setLocationDistricts(prev => ({
        ...prev,
        [locationFilters.locationIndex]: districtsData.data
      }));
    }
  }, [districtsData, locationFilters]);

  // Update location filters when a location is selected for editing
  useEffect(() => {
    if (locations.length > 0 && locationFilters.locationIndex !== null) {
      const currentLocation = locations[locationFilters.locationIndex];
      // Only update the districts when the state and country match the current filters
      if (currentLocation && 
          currentLocation.country === locationFilters.country &&
          currentLocation.state === locationFilters.state) {
        // Districts are now loaded for this location
      }
    }
  }, [districtsData, locationFilters, locations]);

  // Set default country when countries are loaded
  useEffect(() => {
    if (countries.length > 0) {
      const updatedLocations = [...locations];
      
      // Only set default country for empty locations
      for (let i = 0; i < updatedLocations.length; i++) {
        if (updatedLocations[i].country === "") {
          updatedLocations[i].country = countries[0];
        }
      }
      
      if (JSON.stringify(updatedLocations) !== JSON.stringify(locations)) {
        setLocations(updatedLocations);
        
        // Update location filters to trigger state loading
        if (updatedLocations[0] && updatedLocations[0].country) {
          setLocationFilters(prev => ({
            ...prev,
            country: updatedLocations[0].country
          }));
        }
      }
    }
  }, [countries, locations]);

  // Update form when goals change
  useEffect(() => {
    form.setValue("meta.goals", goals);
  }, [goals, form]);

  // Functions for managing goals
  const addGoal = () => {
    setGoals([...goals, ""]);
  };

  const updateGoal = (index: number, value: string) => {
    const updatedGoals = [...goals];
    updatedGoals[index] = value;
    setGoals(updatedGoals);
  };

  const removeGoal = (index: number) => {
    if (goals.length > 1) {
      const updatedGoals = [...goals];
      updatedGoals.splice(index, 1);
      setGoals(updatedGoals);
    }
  };

  // Functions for managing locations
  const addLocation = () => {
    setLocations([...locations, {
      country: countries[0] || "",
      state: "",
      district: "",
      volunteers: 1,
      details: ""
    }]);
  };

  const updateLocation = (index: number, field: keyof Location, value: string | number) => {
    const updatedLocations = [...locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      [field]: value
    };
    setLocations(updatedLocations);

    // Update location filters when country or state changes to trigger API calls
    if (field === 'country') {
      setLocationFilters(prev => ({
        ...prev,
        country: value as string,
        state: null,
        district: null,
        locationIndex: index
      }));
      
      // Reset state and district when country changes
      updatedLocations[index].state = "";
      updatedLocations[index].district = "";
    } else if (field === 'state') {
      setLocationFilters(prev => ({
        ...prev,
        state: value as string,
        district: null,
        locationIndex: index
      }));
      
      // Reset district when state changes
      updatedLocations[index].district = "";
    }
  };

  const removeLocation = (index: number) => {
    if (locations.length > 1) {
      const updatedLocations = [...locations];
      updatedLocations.splice(index, 1);
      setLocations(updatedLocations);
    }
  };

  // Functions for opportunity selection
  const toggleOpportunitySelection = (id: string) => {
    if (selectedOpportunities.includes(id)) {
      setSelectedOpportunities(selectedOpportunities.filter(oppId => oppId !== id));
    } else {
      setSelectedOpportunities([...selectedOpportunities, id]);
    }
  };

  // Validate campaign info step
  const validateCampaignInfo = async () => {
    const campaignInfoFields = ["name", "meta.description", "meta.theme", "active_from", "active_to"];
    const result = await form.trigger(campaignInfoFields as any);
    
    // Check if goals are valid
    const goalsValid = goals.every(goal => goal.trim().length > 0) && goals.length > 0;
    
    setStepErrors(prev => ({
      ...prev,
      campaignInfo: !result || !goalsValid
    }));
    
    return result && goalsValid;
  };

  // Validate opportunities step
  const validateOpportunities = () => {
    const opportunitiesValid = selectedOpportunities.length > 0;
    
    setStepErrors(prev => ({
      ...prev,
      opportunities: !opportunitiesValid
    }));
    
    return opportunitiesValid;
  };

  // Validate locations step
  const validateLocations = () => {
    const locationsValid = locations.every(
      location => 
        location.country.trim().length > 0 && 
        location.state.trim().length > 0 && 
        location.district.trim().length > 0 &&
        location.volunteers > 0
    );
    
    setStepErrors(prev => ({
      ...prev,
      locations: !locationsValid
    }));
    
    return locationsValid;
  };

  // Handle step navigation
  const goToNextStep = async (currentStepValue: string, nextStepValue: string) => {
    let canProceed = false;
    
    if (currentStepValue === "campaign-info") {
      canProceed = await validateCampaignInfo();
    } else if (currentStepValue === "skus-events") {
      canProceed = validateOpportunities();
    }
    
    if (canProceed) {
      setCurrentStep(nextStepValue);
    }
  };

  // Handle form submission
  const handleSubmit = async (data: CampaignFormValues) => {
    const isValid = await validateLocations();
    
    if (isValid) {
      onSubmit(data, goals, locations, selectedOpportunities);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Tabs value={currentStep} onValueChange={setCurrentStep}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="campaign-info">Campaign Info</TabsTrigger>
            <TabsTrigger value="skus-events">Opportunities</TabsTrigger>
            <TabsTrigger value="event-details">Locations</TabsTrigger>
          </TabsList>
          
          {/* Campaign Info Tab */}
          <TabsContent value="campaign-info" className="space-y-6 pt-4">
            {stepErrors.campaignInfo && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please fill in all required fields before proceeding.
                </AlertDescription>
              </Alert>
            )}
            
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Campaign Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter campaign name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="meta.description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter campaign description" 
                      className="min-h-[100px]" 
                      {...field} 
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="meta.theme"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Campaign Theme</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter campaign theme" {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div>
              <FormLabel>Campaign Goals</FormLabel>
              <div className="space-y-2 mt-2">
                {goals.map((goal, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`Goal ${index + 1}`}
                      value={goal}
                      onChange={(e) => updateGoal(index, e.target.value)}
                      className={goals[index].trim().length === 0 ? "border-red-500" : ""}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon" 
                      onClick={() => removeGoal(index)}
                      disabled={goals.length <= 1}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addGoal}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Goal
                </Button>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <FormField
                control={form.control}
                name="active_from"
                render={({ field }) => (
                  <FormItem className="flex flex-col flex-1">
                    <FormLabel>Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="active_to"
                render={({ field }) => (
                  <FormItem className="flex flex-col flex-1">
                    <FormLabel>End Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="flex justify-end">
              <Button 
                type="button"
                onClick={() => goToNextStep("campaign-info", "skus-events")}
              >
                Next: Opportunities
              </Button>
            </div>
          </TabsContent>
          
          {/* Opportunities Tab */}
          <TabsContent value="skus-events" className="space-y-6 pt-4">
            {stepErrors.opportunities && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please select at least one opportunity before proceeding.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="border rounded-md overflow-hidden">
              <div 
                className="max-h-[500px] overflow-y-auto p-4" 
              >
                <OpportunitySelector
                  opportunities={opportunities}
                  selectedOpportunities={selectedOpportunities}
                  isLoadingOpportunities={isLoadingOpportunities}
                  isFetchingNextPage={isFetchingNextPage}
                  hasNextPage={hasNextPage}
                  searchQuery={searchQuery}
                  onSearchChange={onSearchChange}
                  onToggleOpportunitySelection={toggleOpportunitySelection}
                  fetchNextPage={fetchNextPage}
                />
              </div>
            </div>
            
            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setCurrentStep("campaign-info")}
              >
                Back: Campaign Info
              </Button>
              <Button 
                type="button"
                onClick={() => goToNextStep("skus-events", "event-details")}
              >
                Next: Locations
              </Button>
            </div>
          </TabsContent>
          
          {/* Locations Tab */}
          <TabsContent value="event-details" className="space-y-6 pt-4">
            {stepErrors.locations && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please complete all location details before submitting.
                </AlertDescription>
              </Alert>
            )}
            
            <LocationSelector
              locations={locations}
              setLocations={setLocations}
              countries={countries}
              states={states}
              districts={locationDistricts}
              isLoadingCountries={isLoadingCountries}
              isLoadingStates={isLoadingStates}
              isLoadingDistricts={isLoadingDistricts}
              onLocationChange={updateLocation}
              onAddLocation={addLocation}
              onRemoveLocation={removeLocation}
            />
            
            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setCurrentStep("skus-events")}
              >
                Back: Opportunities
              </Button>
              <Button 
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : submitButtonText}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
}
